import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight, Footprints, Send, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import KinglyLogo from "@/components/KinglyLogo";
import { articlesForPath, useHelp } from "@/context/HelpContext";
import { Gate, PlanBadge } from "@/components/Gate";
import AskVideoCard from "@/components/help/AskVideoCard";

/** Header "?" trigger — lives in Layout so it's on every page. */
export function AskKinglyButton({ className = "" }) {
  const { openAsk } = useHelp();
  return (
    <button type="button" onClick={openAsk} data-testid="ask-kingly-btn" title="Ask Kingly — how do I…?" aria-label="Ask Kingly"
      className={`h-8 px-2.5 rounded-md border flex items-center gap-1.5 text-[12px] font-semibold transition-colors
        border-amber-400/60 text-amber-300 hover:bg-amber-400/10 md:border-amber-300 md:text-amber-800 md:bg-amber-50 md:hover:bg-amber-100 ${className}`}>
      <Sparkles className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Ask Kingly</span><span className="sm:hidden">?</span>
      <PlanBadge feature="ask_kingly" className="hidden sm:inline-flex" />
    </button>
  );
}

function Bubble({ m, onGo, onGuide }) {
  if (m.role === "user") {
    return (
      <div className="flex justify-end" data-testid="ask-user-msg">
        <div className="max-w-[85%] rounded-2xl rounded-br-md bg-slate-900 text-white text-sm px-4 py-2.5 leading-relaxed">{m.text}</div>
      </div>
    );
  }
  return (
    <div className="flex gap-2.5" data-testid="ask-assistant-msg">
      <KinglyLogo size={28} className="shrink-0 mt-0.5" />
      <div className="max-w-[88%] space-y-2.5 min-w-0">
        <div className={`rounded-2xl rounded-bl-md text-sm px-4 py-2.5 leading-relaxed whitespace-pre-line ${m.error ? "bg-rose-50 text-rose-800 border border-rose-200" : "bg-zinc-100 text-slate-800"}`}
          data-testid="ask-answer-text">
          {m.text}
        </div>
        {m.route && (
          <Button size="sm" onClick={() => onGo(m.route)} data-testid="ask-take-me-there"
            className="bg-gradient-to-b from-amber-300 to-amber-500 hover:from-amber-200 hover:to-amber-400 text-slate-900 font-semibold border-0 shadow-sm">
            Take me there{m.route_label ? ` — ${m.route_label}` : ""} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        )}
        {m.videos?.length > 0 && (
          <div className="space-y-2" data-testid="ask-videos">
            {m.videos.map((v) => <AskVideoCard key={v.slug} v={v} />)}
          </div>
        )}
        {m.articles?.length > 0 && (
          <div className="space-y-1.5" data-testid="ask-articles">
            {m.articles.map((a) => (
              <div key={a.id} className="rounded-lg border border-zinc-200 bg-white px-3 py-2 flex items-center gap-2" data-testid={`ask-article-${a.id}`}>
                <span className="text-xs font-medium text-slate-700 flex-1 min-w-0 truncate">{a.q}</span>
                {(a.route || a.steps.some((s) => s.route || s.target)) && (
                  <button type="button" onClick={() => onGuide(a)} data-testid={`ask-guide-${a.id}`}
                    className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 hover:bg-amber-100">
                    <Footprints className="w-3 h-3" /> Guide me
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AskKinglySheet() {
  const { askOpen, setAskOpen, messages, setMessages, clearMessages, goTo, startGuide, library, askPrefill, clearPrefill } = useHelp();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const path = location.pathname + location.search;

  useEffect(() => {
    if (askOpen) setTimeout(() => inputRef.current?.focus(), 150);
  }, [askOpen]);
  // Deep link (/help?ask=…) or a nudge: ask the pre-filled question as soon as the sheet opens.
  useEffect(() => {
    if (askOpen && askPrefill) {
      const question = askPrefill;
      clearPrefill();
      setTimeout(() => send(question), 200);
    }
  }, [askOpen, askPrefill]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, busy]);

  const suggestions = articlesForPath(library, path).slice(0, 4);

  async function send(text) {
    const question = (text || q).trim();
    if (!question || busy) return;
    setQ("");
    const history = messages.filter((m) => !m.error).slice(-8).map((m) => ({ role: m.role, text: m.text }));
    setMessages((prev) => [...prev, { role: "user", text: question }]);
    setBusy(true);
    try {
      const { data } = await api.post("/help/ask", { question, path, history });
      setMessages((prev) => [...prev, { role: "assistant", text: data.answer, articles: data.articles, route: data.route, route_label: data.route_label, videos: data.videos }]);
    } catch (e) {
      const detail = e.response?.data?.detail || "Ask Kingly couldn't answer just now — please try again.";
      setMessages((prev) => [...prev, { role: "assistant", text: detail, error: true }]);
      if (e.response?.status === 429) toast.error(detail);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open={askOpen} onOpenChange={setAskOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col bg-white [&>button]:text-white [&>button]:opacity-90 [&>button]:top-[calc(env(safe-area-inset-top)+1.1rem)]" data-testid="ask-kingly-sheet">
        <VisuallyHidden>
          <SheetTitle>Ask Kingly</SheetTitle>
          <SheetDescription>Ask how to do anything in Kingly</SheetDescription>
        </VisuallyHidden>
        <div className="px-5 pt-[calc(env(safe-area-inset-top)+1rem)] pb-4 bg-slate-900 text-white">
          <div className="flex items-center gap-3">
            <KinglyLogo size={40} />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300 font-semibold">Kingly · Help</div>
              <div className="font-display font-bold text-lg leading-tight">Ask Kingly</div>
            </div>
            {messages.length > 0 && (
              <button type="button" onClick={clearMessages} data-testid="ask-clear-btn" title="Start over"
                className="mr-8 w-8 h-8 rounded-full flex items-center justify-center text-slate-300 hover:bg-white/10">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
          <p className="mt-2 text-xs text-slate-300">Plain-English answers from the help library, with a button that takes you to the right screen.</p>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="ask-messages">
          <Gate feature="ask_kingly" compact><></></Gate>
          {messages.length === 0 && (
            <div className="space-y-3" data-testid="ask-suggestions">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Try asking</p>
              {suggestions.map((a) => (
                <button key={a.id} type="button" onClick={() => send(a.q)} data-testid={`ask-suggestion-${a.id}`}
                  className="w-full text-left text-sm rounded-xl border border-zinc-200 bg-zinc-50 hover:bg-amber-50 hover:border-amber-300 px-4 py-3 text-slate-800 transition-colors">
                  {a.q}
                </button>
              ))}
              <button type="button" onClick={() => goTo("/help")} data-testid="ask-open-help-center"
                className="text-xs font-semibold text-amber-700 hover:underline">Browse the full Help Center →</button>
            </div>
          )}
          {messages.map((m, i) => <Bubble key={i} m={m} onGo={goTo} onGuide={startGuide} />)}
          {busy && (
            <div className="flex gap-2.5 items-center text-sm text-slate-500" data-testid="ask-thinking">
              <KinglyLogo size={28} className="shrink-0" />
              <span className="inline-flex gap-1 items-center">Kingly is thinking
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-bounce" />
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-bounce [animation-delay:120ms]" />
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-bounce [animation-delay:240ms]" />
              </span>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <form onSubmit={(e) => { e.preventDefault(); send(); }}
          className="border-t border-zinc-200 p-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] flex items-end gap-2 bg-white">
          <textarea ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} rows={1} data-testid="ask-input"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="How do I…?" maxLength={600}
            className="flex-1 resize-none rounded-xl border border-zinc-300 bg-zinc-50 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 max-h-32" />
          <Button type="submit" disabled={busy || !q.trim()} data-testid="ask-send-btn" aria-label="Send"
            className="h-10 w-10 p-0 rounded-xl bg-slate-900 hover:bg-slate-800 text-white">
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}
